import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { HandCoins, Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Payments() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientFilter, setClientFilter] = useState("");
    const [loading, setLoading] = useState(true);
    // Iter 45: client-side text search — same UX contract as Clients/AMC.
    const [textQuery, setTextQuery] = useState("");

    const load = async () => {
        try {
            const params = {};
            if (clientFilter) params.client_id = clientFilter;
            const [pay, cls] = await Promise.all([
                api.get("/billing/payment/history", { params }),
                api.get("/billing/client/list"),
            ]);
            setItems(pay.data.items || []);
            setClients(cls.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load payments"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientFilter]);

    // Iter 45: multi-field client-side filter — invoice #, client, mode,
    // reference and remarks (the columns operators eyeball during
    // reconciliation).
    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((p) => {
            const inv = (p.invoice_number || "").toLowerCase();
            const client = (p.client?.name || "").toLowerCase();
            const mode = (p.payment_mode || "").toLowerCase();
            const ref = (p.reference_number || "").toLowerCase();
            const remarks = (p.remarks || "").toLowerCase();
            return (
                inv.includes(q) || client.includes(q) || mode.includes(q) ||
                ref.includes(q) || remarks.includes(q)
            );
        });
    }, [items, textQuery]);

    // Iter 61 — A payment row's "displayed amount" is the CASH that actually
    // hit the bank: receipt_amount for cross-currency receipts, else `amount`
    // in the invoice currency. The Payments page is operationally a CASH-FLOW
    // view (not a receivables view) — operators reconciling bank statements
    // need to see the literal ₹ that landed in the account.
    const rowDisplay = (p) => {
        if (p.is_cross_currency && p.receipt_currency && p.receipt_amount) {
            return { amount: Number(p.receipt_amount), currency: p.receipt_currency };
        }
        return { amount: Number(p.amount || 0), currency: p.invoice_currency || "INR" };
    };

    // Iter 61 — Total tile sums INR-equivalent receipts only. Non-INR rows
    // are excluded from the headline number (to avoid "₹ symbol over a
    // mixed bag of currencies" misleading the operator) and surfaced
    // separately as a small chip below. No FX engine — strictly literal.
    const { inrTotal, otherTotals } = useMemo(() => {
        let inr = 0;
        const others = {};
        for (const p of visibleItems) {
            const { amount, currency } = rowDisplay(p);
            if (currency === "INR") inr += amount;
            else others[currency] = (others[currency] || 0) + amount;
        }
        return {
            inrTotal: inr,
            otherTotals: Object.entries(others).map(([c, v]) => ({ currency: c, amount: v })),
        };
    }, [visibleItems]);

    return (
        <div data-testid="payments-page">
            <PageHeader
                eyebrow="Accounts · Phase 1"
                title="Payments"
                description="Receipts logged across invoices. Add a payment from the invoice detail screen."
            />
            <div className="px-6 sm:px-8 py-8 space-y-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="reedos-card p-5" data-testid="payments-stat-count">
                        <div className="reedos-eyebrow">Receipts</div>
                        <div className="font-display text-3xl font-bold mt-2 reedos-data">{visibleItems.length}</div>
                    </div>
                    <div className="reedos-card p-5" data-testid="payments-stat-total">
                        <div className="reedos-eyebrow">Collected (filtered)</div>
                        <div className="font-display text-3xl font-bold mt-2 reedos-data text-emerald-700" data-testid="payments-stat-total-value">{formatINR(inrTotal, { compact: true })}</div>
                        {otherTotals.length > 0 && (
                            <div className="text-[11px] text-slate-500 mt-1.5 flex flex-wrap gap-1.5" data-testid="payments-stat-total-other-ccy">
                                {otherTotals.map((o) => (
                                    <span key={o.currency} className="inline-flex items-center rounded bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-px font-semibold">
                                        + {o.currency} {o.amount.toLocaleString("en-IN")}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3" data-testid="payments-filters">
                    {/* Iter 45: text search — same placement/style as Clients.jsx */}
                    <div className="relative flex-1 min-w-[260px] max-w-md">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={textQuery}
                            onChange={(e) => setTextQuery(e.target.value)}
                            placeholder="Search by invoice, client, mode, reference or remarks…"
                            className="pl-8 h-9 text-sm"
                            data-testid="payments-search-input"
                        />
                    </div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="payments-client-filter">
                        <option value="">All clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {textQuery && (
                        <span className="text-xs text-slate-500" data-testid="payments-search-count">
                            {visibleItems.length} of {items.length}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500">Loading payments...</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<HandCoins size={40} weight="thin" />}
                        title="No payments recorded"
                        description="Open any invoice and click 'Record payment' to log a receipt."
                        data-testid="payments-empty"
                    />
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<MagnifyingGlass size={40} weight="thin" />}
                        title="No matching payments"
                        description="Try a different search term or clear the filter."
                        data-testid="payments-no-match"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="payments-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Invoice</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Date</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Mode</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Reference</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    <th className="px-5 py-3 reedos-eyebrow"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((p) => {
                                    // Iter 61 — actual cash that hit the bank wins on this view.
                                    const disp = rowDisplay(p);
                                    return (
                                    <tr key={p.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`payment-row-${p.id}`}>
                                        <td className="px-5 py-3">
                                            <Link to={`/invoices/${p.invoice_id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                {p.invoice_number}
                                            </Link>
                                        </td>
                                        <td className="px-5 py-3 text-slate-900">{p.client?.name}</td>
                                        <td className="px-5 py-3 text-xs text-slate-600">{formatDate(p.payment_date)}</td>
                                        <td className="px-5 py-3"><StatusBadge status="Paid" className="!bg-slate-100 !text-slate-700 !border-slate-300">{p.payment_mode}</StatusBadge></td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-600">{p.reference_number || "—"}</td>
                                        <td
                                            className="px-5 py-3 text-right reedos-data font-semibold text-emerald-700"
                                            title={p.is_cross_currency
                                                ? `Invoice settled for ${p.invoice_currency || "INR"} ${Number(p.amount || 0).toLocaleString("en-IN")} · actual receipt ${disp.currency} ${disp.amount.toLocaleString("en-IN")} (no FX gain/loss tracked)`
                                                : undefined
                                            }
                                            data-testid={`payment-row-amount-${p.id}`}
                                        >
                                            <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                                                <span>{formatINR(disp.amount, disp.currency)}</span>
                                                {/* Iter 62 — Partial-payment chip. Only renders when the
                                                    parent invoice is still partially settled. Click-through
                                                    to invoice detail (same target as the invoice-number link). */}
                                                {p.invoice_status === "Partial" && (
                                                    <Link
                                                        to={`/invoices/${p.invoice_id}`}
                                                        className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-px text-[10px] font-semibold hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                                        data-testid={`payment-row-partial-chip-${p.id}`}
                                                        title="Open invoice — partially settled"
                                                    >
                                                        Partial
                                                    </Link>
                                                )}
                                                {p.invoice_status === "Unpaid" && (
                                                    <Link
                                                        to={`/invoices/${p.invoice_id}`}
                                                        className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-px text-[10px] font-semibold hover:bg-rose-100 hover:border-rose-300 transition-colors"
                                                        data-testid={`payment-row-unpaid-chip-${p.id}`}
                                                        title="Open invoice — unpaid"
                                                    >
                                                        Unpaid
                                                    </Link>
                                                )}
                                            </div>
                                            {/* Iter 61 — cross-currency invoice-equivalent subtitle (preserved). */}
                                            {p.is_cross_currency && (
                                                <Link
                                                    to={`/invoices/${p.invoice_id}`}
                                                    className="block text-[10px] font-normal text-slate-500 mt-0.5 hover:text-slate-700 hover:underline"
                                                    data-testid={`payment-row-invoice-equiv-${p.id}`}
                                                >
                                                    ≈ {p.invoice_currency || "INR"} {Number(p.amount || 0).toLocaleString("en-IN")} on invoice
                                                </Link>
                                            )}
                                            {/* Iter 62 — reconciliation context subtitle. Surfaces
                                                "Nth of M · ₹X outstanding" so operators can match each
                                                payment row against the parent invoice's full payment
                                                trail at a glance. Skipped when the invoice is fully
                                                paid in one shot (no useful info to show). */}
                                            {p.invoice_status !== "Paid" && p.payment_count > 0 && (
                                                <Link
                                                    to={`/invoices/${p.invoice_id}`}
                                                    className="block text-[10px] font-normal text-slate-500 mt-0.5 hover:text-slate-700 hover:underline"
                                                    data-testid={`payment-row-partial-context-${p.id}`}
                                                >
                                                    {p.payment_count > 1
                                                        ? `${p.payment_index} of ${p.payment_count} · `
                                                        : ""}
                                                    {formatINR(Math.max(0, Number(p.invoice_outstanding || 0)), p.invoice_currency)} left
                                                    {" on invoice "}
                                                    {formatINR(Number(p.invoice_total_amount || 0), p.invoice_currency)}
                                                </Link>
                                            )}
                                            {/* Iter 62 — fully-paid invoice with multiple settlements:
                                                still show the sequence number for reconciliation context. */}
                                            {p.invoice_status === "Paid" && p.payment_count > 1 && !p.is_cross_currency && (
                                                <span className="block text-[10px] font-normal text-slate-400 mt-0.5" data-testid={`payment-row-final-context-${p.id}`}>
                                                    {p.payment_index} of {p.payment_count} · invoice closed
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">{p.remarks || ""}</td>
                                    </tr>
                                );})}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
